import { useMemo } from "react";
import SliceLink from "./SliceLink";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { builtInToolName } from "../../../api/content/processDefinitions";
import type { CheckRun, CheckStatus } from "../../../api/content/reviewDecoders";
import type { ToolTurnFinishedPayload, ToolTurnRequestedPayload, ToolValueOutput } from "../../../api/threads/liveMessages";
import { readableJsonText } from "../../../shared/formatting/readableJson";
import { appPaths } from "../../../shared/routing/appPaths";
import ErrorNotice from "../../../errors/ErrorNotice";
import CopyReference from "../../../ui/content/CopyReference";
import RunMarkdown from "./RunMarkdown";
import DefinitionListItem from "../../../ui/layout/DefinitionListItem";
import CheckResultList from "../../reviews/checks/CheckResultList";
import { useThreadContext } from "../page/threadPageContext";
import { recordedProblemError } from "./recordedProblems";
import { checkCounts, formatDuration, hasUsage, runDurationMilliseconds, valuePreview, type RankedResult } from "./toolValues";
import { CostLine, UsageLine } from "./VersionsUsedList";
import Chip from "../../../ui/feedback/Chip";
import { TURN_OUTCOME_TONE } from "../status/turnOutcomeTone";
import SourceView from "../../../ui/content/source/SourceView";
import ReleaseMade from "../../knowledgeData/releases/ReleaseMade";
import LongTextFold from "./LongTextFold";
import DocumentVersionName from "../../sources/document/DocumentVersionName";
import { segmentDisplayName } from "../../sources/document/documentNames";
import { termName, useProjectTermCards } from "../../ontology/terms/termNames";
import { formatDocumentAddress, parseCollectionVersionRef, parseContentAddress } from "../../../shared/references/contentReferences";

/**
 * Entries of tool turns (processes.md P-1, P-6, P-7): the frozen run (tool, runtime,
 * parameters, the inputs with what they read), and its result (outcome, runtime, whether
 * recorded outputs were reused, duration, and the output entries). Each output is its own
 * `step_output` entry; a proposal links to its review.
 */

function ToolName({ tool, name }: { tool: string; name: string }) {
  const { t } = useTranslation();
  const builtIn = builtInToolName(tool);
  if (builtIn !== null) {
    return <span className="font-mono">{t("workflows.node.builtInTool", { tool: builtIn })}</span>;
  }
  return <Link to={appPaths.item(tool)} className="app-link app-link-cue-away font-mono underline">{name}</Link>;
}

export function ToolTurnRequestedBody({ payload }: { payload: ToolTurnRequestedPayload }) {
  const { t } = useTranslation();
  const { stepName } = useThreadContext();
  const route = payload.route.kind === "built_in" ? t("threads.toolTurn.onServer") : t("threads.toolTurn.onRuntime", { runtime: payload.route.runtime });
  const inputUses = new Map(payload.versions_used.inputs.map((use) => [use.input, use]));
  return (
    <>
      <span>
        {payload.step_key === null ? t("threads.toolTurn.requestedWithoutStep", { route }) : t("threads.toolTurn.requested", { step: stepName(payload.step_key, payload.step), route })}{" "}
        <ToolName tool={payload.run.tool} name={payload.run.tool_name} />
      </span>
      <details className="mt-1">
        <summary className="app-link cursor-pointer text-[11px]">{t("threads.turn.details")}</summary>
        <div className="mt-2 space-y-3 text-xs">
          {payload.permission_profile === null ? null : (
            <dl>
              <DefinitionListItem term={t("threads.turn.permissionProfile")}>{payload.permission_profile}</DefinitionListItem>
            </dl>
          )}
          {Object.keys(payload.run.parameters).length === 0 ? null : (
            <div>
              <p className="app-text-muted text-[11px] font-semibold">{t("threads.toolTurn.parameters")}</p>
              <SourceView label={t("threads.toolTurn.parameters")} language="json" value={readableJsonText(payload.run.parameters, { sortKeys: false })} className="mt-1" />
            </div>
          )}
          <ul className="space-y-1">
            {Object.entries(payload.run.inputs).map(([name, value]) => {
              const use = inputUses.get(name);
              return (
                <li key={name}>
                  <span className="font-mono">{name}</span>{" "}
                  <Chip size="compact">{t(`threads.toolTurn.valueKinds.${value.kind}`)}</Chip>
                  {use === undefined || use.input_entry === null ? null : <span className="app-text-muted"> · #{use.input_entry}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      </details>
    </>
  );
}

export function ToolTurnFinishedBody({ payload }: { payload: ToolTurnFinishedPayload }) {
  const { t, i18n } = useTranslation();
  const { threadId } = useThreadContext();
  const duration = runDurationMilliseconds(payload.started_at, payload.finished_at);
  return (
    <div className="space-y-2">
      {payload.problem === null ? null : <ErrorNotice error={recordedProblemError(payload.problem)} />}
      <ToolTurnOutcome payload={payload} />
      {payload.output_entries.length === 0 ? null : (
        <p className="text-xs">
          {t("threads.toolTurn.outputs")}{" "}
          {payload.output_entries.map((number, index) => (
            <span key={number}>
              {index === 0 ? "" : ", "}
              <Link to={appPaths.thread(threadId, number)} className="app-link app-link-cue-up font-mono underline">#{number}</Link>
            </span>
          ))}
        </p>
      )}
      <details>
        <summary className="app-link cursor-pointer text-[11px]">{t("threads.turn.details")}</summary>
        <dl className="mt-2 space-y-1 text-xs">
          <DefinitionListItem term={t("threads.turn.outcome")}>{t(`threads.turn.outcomes.${payload.outcome}`)}</DefinitionListItem>
          {duration === null ? null : <DefinitionListItem term={t("threads.toolTurn.duration")}>{formatDuration(duration, i18n.resolvedLanguage)}</DefinitionListItem>}
          {hasUsage(payload.usage) ? (
            <DefinitionListItem term={t("threads.usage.title")}><UsageLine usage={payload.usage} /></DefinitionListItem>
          ) : null}
          {Object.entries(payload.input_files ?? {}).map(([input, file]) => (
            <DefinitionListItem key={input} term={t("threads.toolTurn.inputFileLabel", { input })} valueClassName="break-all">
              {file.path}
            </DefinitionListItem>
          ))}
        </dl>
      </details>
    </div>
  );
}

/**
 * What the reader needs of a finished tool run: the outcome when it isn't the expected one,
 * whether recorded outputs were reused, and what model calls cost.
 */
export function ToolTurnOutcome({ payload }: { payload: ToolTurnFinishedPayload }) {
  const { t } = useTranslation();
  // A tool that called no model has nothing to count.
  const cost = hasUsage(payload.usage) ? <CostLine usage={payload.usage} /> : null;
  if (payload.outcome === "completed" && !payload.memoized && cost === null) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px]">
      {payload.outcome === "completed" ? null : (
        <Chip tone={TURN_OUTCOME_TONE[payload.outcome]}>{t(`threads.turn.outcomes.${payload.outcome}`)}</Chip>
      )}
      {payload.memoized ? <Chip>{t("threads.toolTurn.memoized")}</Chip> : null}
      {cost}
    </div>
  );
}

/** A ranking, best first: each result's score, label or reference (linked when it leads somewhere) and snippet. */
function RankedResultList({ results, more }: { results: readonly RankedResult[]; more: number }) {
  const { t, i18n } = useTranslation();
  const score = useMemo(() => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 }), [i18n.language]);
  return (
    <ol className="list-decimal space-y-1 pl-5 text-xs">
      {results.map((result) => {
        const name = result.label ?? (result.href === null ? <RankedTermName iri={result.reference} /> : result.reference);
        return (
          <li key={result.reference}>
            <Chip size="compact" className="mr-1 font-mono">{t("threads.toolTurn.score", { score: score.format(result.score) })}</Chip>
            {result.href === null ? (
              <span className="break-all">{name}</span>
            ) : (
              <Link to={result.href} className="app-link app-link-cue-away break-all underline">{name}</Link>
            )}
            {result.snippet === null ? null : <p className="app-text-muted whitespace-pre-wrap">{result.snippet}</p>}
          </li>
        );
      })}
      {more === 0 ? null : <li className="app-text-muted list-none">{t("threads.toolTurn.more", { count: more })}</li>}
    </ol>
  );
}

/** Term names come from the model the Thread runs against; the output's JSON retains IRIs. */
function RankedTermName({ iri }: { iri: string }) {
  const { i18n } = useTranslation();
  const { overview } = useThreadContext();
  const commit = parseCollectionVersionRef(overview.bindings.content_version)?.commit ?? null;
  const cards = useProjectTermCards(overview.projectId, commit);
  return <>{termName(iri, cards, i18n.resolvedLanguage ?? "en")}</>;
}

/** One output of a tool run: its name and kind, a short view, and the value as JSON. */
export function ToolValueBody({ part, output, entry }: { part: string; output: ToolValueOutput; entry?: number }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1 text-sm">
      <p>
        {t("threads.toolTurn.output", { part })}{" "}
        <Chip size="compact">{t(`threads.toolTurn.valueKinds.${output.value_kind}`)}</Chip>
      </p>
      <ToolValuePreview output={output} entry={entry} />
      <details>
        <summary className="app-link cursor-pointer text-[11px]">{t("threads.toolTurn.value")}</summary>
        <SourceView label={t("threads.toolTurn.value")} language="json" value={readableJsonText(output.value, { sortKeys: false })} className="mt-1" />
        <p className="app-text-muted mt-1 break-all font-mono text-[10px]">{output.digest}</p>
      </details>
    </div>
  );
}

/**
 * A value at a glance. In a step's summary (`summary`), a check report reads as counts and
 * the checks that didn't pass; the output entry lists every check. Ontology terms recorded as
 * the output `entry` open as a lens on the Data model.
 */
export function ToolValuePreview({ output, summary = false, entry }: { output: ToolValueOutput; summary?: boolean; entry?: number }) {
  const { t } = useTranslation();
  const preview = valuePreview(output.value_kind, output.value);
  return (
    <>
      {preview.kind === "text" && output.value_kind === "text" ? <LongTextFold text={preview.text} show={(text) => <RunMarkdown content={text} className="app-markdown text-xs" />} /> : null}
      {preview.kind === "text" && output.value_kind !== "text" ? <LongTextFold text={preview.text} show={(text) => <p className="whitespace-pre-wrap text-xs">{text}</p>} /> : null}
      {preview.kind === "documents" ? <p className="text-xs">{t("threads.toolTurn.documents", { count: preview.count })}</p> : null}
      {output.value_kind === "release" ? <ReleaseMade value={output.value} /> : null}
      {output.value_kind === "term_cards" && entry !== undefined ? <SliceLink value={output.value} entry={entry} /> : null}
      {preview.kind === "units" ? (
        <ul className="space-y-0.5 text-xs">
          {preview.units.map((unit) => {
            const address = parseContentAddress(unit.reference);
            return (
              <li key={unit.reference}>
                {address === null ? <span className="break-all font-mono text-[10px]">{unit.reference}</span> : (
                  <>
                    <DocumentVersionName reference={formatDocumentAddress(address.document)} />
                    {address.segmentPath === null ? null : ` · ${segmentDisplayName(address.segmentPath)}`}
                  </>
                )}
                {" "}{unit.text}
              </li>
            );
          })}
          {preview.more === 0 ? null : <li className="app-text-muted">{t("threads.toolTurn.more", { count: preview.more })}</li>}
        </ul>
      ) : null}
      {preview.kind === "ranked" ? <RankedResultList results={preview.results} more={preview.more} /> : null}
      {preview.kind === "item" ? (
        <p className="text-xs">
          {preview.reference === null ? (
            t("threads.toolTurn.newItem", { kind: preview.itemKind ?? "" })
          ) : (
            <>
              <Link to={appPaths.item(preview.reference)} className="app-link app-link-cue-away underline">{t("threads.toolTurn.openItem")}</Link>
              <CopyReference value={preview.reference} className="ml-1 inline-flex align-middle" />
            </>
          )}
        </p>
      ) : null}
      {preview.kind === "checks" && summary ? <CheckSummary run={preview.run} /> : null}
      {preview.kind === "checks" && !summary ? (
        <div className="space-y-1">
          <CheckResultList results={preview.run.results} />
          {preview.run.processResults ? (
            <>
              <p className="app-title text-xs font-semibold">{t("reviews.checks.process")}</p>
              <CheckResultList results={preview.run.processResults} />
            </>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/** "3 passed", or "1 failed, 2 passed" with the checks that didn't pass listed. */
function CheckSummary({ run }: { run: CheckRun }) {
  const { t } = useTranslation();
  const results = [...run.results, ...(run.processResults ?? [])];
  const notPassed = results.filter((result) => result.status !== "pass");
  return (
    <div className="space-y-1">
      <p className="text-sm">
        {checkCounts(results).map(({ status, count }) => t(CHECK_COUNT_KEYS[status], { count })).join(", ")}
      </p>
      {notPassed.length === 0 ? null : <CheckResultList results={notPassed} />}
    </div>
  );
}

const CHECK_COUNT_KEYS: Record<CheckStatus, string> = {
  fail: "threads.toolStep.checks.failed",
  cannot_run: "threads.toolStep.checks.cannotRun",
  pass: "threads.toolStep.checks.passed",
  not_checked: "threads.toolStep.checks.notChecked",
};
